import { defineStore } from "pinia";
import http, { fetchSanctumCsrfCookie } from "../services/http";
import { queryClient } from "@/queryClient";
import type { AuthAccessContext } from "@/types/authAccess";
import type { FirstEntryAuthStatus } from "@/types/clientFirstEntry";

/** Internal staff row from /api/auth/login or /api/auth/me */
export interface InternalUserPayload {
  id: number;
  u_id?: string | null;
  name?: string | null;
  alias?: string | null;
  email?: string | null;
  hr_designation?: string | null;
  is_ccex?: boolean;
  user_type?: string | number | null;
  user_level?: string | number | null;
  role_access?: string | number | null;
  team_id?: string | null;
  team_name?: string | null;
  sub_team_id?: string | null;
  sub_team_name?: string | null;
  role_id?: number | null;
  role_key?: string | null;
  is_active?: boolean;
  status?: string | null;
}

/** Client portal row (no WordPress password) */
export interface ClientProfilePayload {
  id: number;
  first_entry?: FirstEntryAuthStatus | null;
  client_id: string;
  company_name?: string | null;
  email?: string | null;
  services?: string[] | null;
  website_url?: string | null;
  source_date_added?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  client_account_status?: string | null;
  role_id?: number | null;
  role_key?: string | null;
  whatsapp_notifications_enabled?: boolean;
  whatsapp_number?: string | null;
}

export type AccountType = "internal_user" | "client" | null;

import { clearAllModuleCaches } from "@/composables/useCachedResource";

function safeString(v: unknown): string {
  return v === null || v === undefined ? "" : String(v);
}

const TASK_ONLY_LEVELS = new Set(["2", "3", "6", "8", "9"]);
const DASHBOARD_USER_TYPES = new Set(["2", "3"]);

export const useAuthStore = defineStore("auth", {
  state: () => ({
    hydrated: false,
    accountType: null as AccountType,
    internalUser: null as InternalUserPayload | null,
    clientProfile: null as ClientProfilePayload | null,
    accessContext: null as AuthAccessContext | null,
    /** Last login identifier (email / wp username) — memory only, optional */
    loginIdentifier: "" as string,
  }),

  getters: {
    isAuthenticated: (s): boolean =>
      s.accountType !== null &&
      (s.internalUser !== null || s.clientProfile !== null),

    /** Legacy SideNavigation / Header: "user" | "client" */
    userType(): "user" | "client" | "" {
      if (this.accountType === "internal_user") return "user";
      if (this.accountType === "client") return "client";
      return "";
    },

    /** DB user_type column (internal) — string for comparisons */
    userRole(): string {
      if (!this.internalUser) return "";
      return safeString(this.internalUser.user_type);
    },

    userLevel(): string {
      if (!this.internalUser) return "";
      return safeString(this.internalUser.user_level);
    },

    roleAccess(): string {
      if (!this.internalUser) return "";
      return safeString(this.internalUser.role_access);
    },

    isSuperAdmin(): boolean {
      if (this.accessContext?.role?.is_superadmin) {
        return true;
      }

      return (
        this.accountType === "internal_user" &&
        (this.roleAccess === "1" ||
          (this.userLevel === "1" && this.userRole === "1"))
      );
    },

    isProdOrMktgAdmin(): boolean {
      const isAdminLevel =
        this.roleAccess === "2" || ["1", "2", "6", "9"].includes(this.userLevel);
      return (
        this.accountType === "internal_user" &&
        isAdminLevel &&
        !this.isSuperAdmin
      );
    },

    isCcex(): boolean {
      const designation = safeString(this.internalUser?.hr_designation)
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");

      return (
        this.accountType === "internal_user" &&
        !this.isSuperAdmin &&
        !this.isProdOrMktgAdmin &&
        (this.internalUser?.is_ccex === true ||
          designation === "ccex" ||
          designation.includes("client care executive"))
      );
    },

    canEditRenewalTracking(): boolean {
      if (this.isSuperAdmin) {
        return true;
      }

      const isNormalAdmin =
        this.accountType === "internal_user" &&
        ["2", "6", "9"].includes(this.userLevel);
      const effectivePermissions =
        this.accessContext?.permission_access
          ?.effective_allowed_permission_keys || [];

      return isNormalAdmin && effectivePermissions.includes("clients.edit");
    },

    isGlobalChatView(): boolean {
      return this.isSuperAdmin || this.isProdOrMktgAdmin;
    },

    isChatReadOnly(): boolean {
      return this.isSuperAdmin;
    },

    isTaskOnlyInternalUser(): boolean {
      return (
        this.accountType === "internal_user" &&
        TASK_ONLY_LEVELS.has(this.userLevel) &&
        !DASHBOARD_USER_TYPES.has(this.userRole)
      );
    },

    companyName(): string {
      if (this.accountType === "internal_user" && this.internalUser) {
        if (this.userLevel === "1")
          return safeString(this.internalUser.name) || "Proweaver";
        return (
          safeString(this.internalUser.alias) ||
          safeString(this.internalUser.name) ||
          "User"
        );
      }
      if (this.clientProfile) {
        return safeString(this.clientProfile.company_name) || "Client";
      }
      return "";
    },

    displayEmail(): string {
      if (this.internalUser?.email) return safeString(this.internalUser.email);
      if (this.clientProfile?.email)
        return safeString(this.clientProfile.email);
      return "";
    },

    /** Primary key for client-sidenav API (clients.id) */
    clientSidenavUserId(): string | number | null {
      if (this.accountType === "client" && this.clientProfile) {
        return this.clientProfile.id;
      }
      if (this.accountType === "internal_user" && this.internalUser) {
        return this.internalUser.id;
      }
      return null;
    },

    orchestratorUid(): string {
      return safeString(this.internalUser?.u_id);
    },
  },

  actions: {
    clearSession() {
      this.accountType = null;
      this.internalUser = null;
      this.clientProfile = null;
      this.accessContext = null;
      queryClient.clear();
      clearAllModuleCaches();
      this.hydrated = true;
    },

    applyMePayload(data: {
      account_type: string;
      user?: InternalUserPayload;
      client?: ClientProfilePayload;
      access_context?: AuthAccessContext | null;
    }) {
      this.accessContext = data.access_context || null;

      if (data.account_type === "internal_user" && data.user) {
        this.accountType = "internal_user";
        this.internalUser = data.user;
        this.clientProfile = null;
        queryClient.invalidateQueries({ queryKey: ["sidenav"] });
        return;
      }
      if (data.account_type === "client" && data.client) {
        this.accountType = "client";
        this.clientProfile = data.client;
        this.internalUser = null;
        queryClient.invalidateQueries({ queryKey: ["sidenav"] });
        return;
      }
      this.clearSession();
    },

    async ensureCsrfCookie(): Promise<void> {
      await fetchSanctumCsrfCookie();
    },

    async restoreSession(): Promise<boolean> {
      try {
        const { data } = await http.get<{
          account_type: string;
          user?: InternalUserPayload;
          client?: ClientProfilePayload;
          access_context?: AuthAccessContext | null;
        }>("/api/auth/me");
        this.applyMePayload(data);
        this.hydrated = true;
        return this.isAuthenticated;
      } catch {
        this.clearSession();
        return false;
      }
    },

    async login(payload: {
      email: string;
      password: string;
      remember?: boolean;
    }): Promise<{
      account_type: string;
      token?: string | null;
      user?: InternalUserPayload;
      client?: ClientProfilePayload;
      access_context?: AuthAccessContext | null;
    }> {
      await this.ensureCsrfCookie();
      const { data } = await http.post<{
        message?: string;
        account_type: string;
        token?: string | null;
        user?: InternalUserPayload;
        client?: ClientProfilePayload;
        access_context?: AuthAccessContext | null;
      }>("/api/auth/login", {
        email: payload.email,
        password: payload.password,
        remember: !!payload.remember,
      });
      this.loginIdentifier = payload.email.toLowerCase();
      this.applyMePayload(data);
      this.hydrated = true;
      return data;
    },

    async logout(): Promise<void> {
      try {
        await this.ensureCsrfCookie();
        await http.post("/api/logout");
      } catch {
        /* still clear local state */
      }
      this.clearSession();
    },
  },
});
